import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Download,
  Loader2,
  Megaphone,
  Paperclip,
  Printer,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import {
  MATERIAL_FORMATS,
  type MarketingMaterialDTO,
  type MaterialSize,
} from "@app/shared";
import {
  api,
  materialAttachmentUrl,
  materialImageUrl,
  materialPdfUrl,
} from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  ImageSourceSelect,
  useImageSourceState,
} from "../components/ImageSourceSelect";
import { useToast } from "../components/Toast";

const FORMATS = Object.values(MATERIAL_FORMATS);

/**
 * Materi: generate offline marketing collateral (flyer, poster, spanduk/banner,
 * x-banner, brosur). NOT linked to social/web posting — the operator writes a
 * brief, AI lays out the copy (keeping the brief's facts), and we render a
 * high-res PNG + print-ready PDF to download.
 */
export function Materi() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();
  const [format, setFormat] = useState<MaterialSize>("flyer");
  const [brief, setBrief] = useState("");
  const [withPhoto, setWithPhoto] = useState(true);
  const [imageSource, setImageSource] = useImageSourceState();
  const [imageQuery, setImageQuery] = useState("");
  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const [fileKey, setFileKey] = useState(0); // bump to reset the file <input>

  const isCustom = format === "custom";
  const customWNum = Number(customW);
  const customHNum = Number(customH);
  const customValid =
    !isCustom ||
    (customWNum >= 5 && customWNum <= 500 && customHNum >= 5 && customHNum <= 500);

  const materialsQuery = useQuery({
    queryKey: ["materials", accountKey],
    queryFn: ({ signal }) => api.materials({ accountKey: accountKey! }, signal),
    enabled: !!accountKey,
    // Keep refetching while anything is still rendering.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((m) => m.status === "generating")
        ? 4000
        : false,
  });

  const generate = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.generateMaterial(
        {
          accountKey,
          format,
          brief: brief.trim(),
          withPhoto,
          imageSource: withPhoto ? imageSource : undefined,
          imageQuery:
            withPhoto && imageQuery.trim() ? imageQuery.trim() : undefined,
          customWidthCm: isCustom ? customWNum : undefined,
          customHeightCm: isCustom ? customHNum : undefined,
        },
        attachFiles,
      );
    },
    onSuccess: () => {
      toast.success("Materi sedang dibuat… (muncul di bawah, ±20–40 detik)");
      setAttachFiles([]);
      setFileKey((k) => k + 1);
      void queryClient.invalidateQueries({ queryKey: ["materials"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal membuat materi"),
  });

  const materials = materialsQuery.data ?? [];
  const canGenerate =
    !!accountKey && brief.trim().length > 0 && customValid && !generate.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-navy">
            <Megaphone className="h-6 w-6 text-teal" />
            Materi pemasaran
          </h1>
          <p className="text-sm text-slate-500">
            Buat flyer, poster, spanduk, x-banner, brosur untuk kebutuhan cetak/
            offline. Bukan untuk posting sosmed/web. Hasil: PNG + PDF siap cetak.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buat materi</CardTitle>
          <CardDescription>
            Pilih format & tulis brief (tujuan, info acara/promo, tanggal,
            lokasi, kontak…). Fakta yang Anda tulis dipakai apa adanya — AI tidak
            mengarang angka/klaim.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="mat-format">Format</Label>
              <select
                id="mat-format"
                value={format}
                onChange={(e) => setFormat(e.target.value as MaterialSize)}
                disabled={generate.isPending}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-1"
              >
                {FORMATS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} — {f.hint}
                  </option>
                ))}
                <option value="custom">Custom — ukuran sendiri (cm)</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex h-10 cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={withPhoto}
                  disabled={generate.isPending}
                  onChange={(e) => setWithPhoto(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-teal focus:ring-teal"
                />
                Pakai foto latar
              </label>
            </div>
          </div>

          {isCustom ? (
            <div className="flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="mat-cw">Lebar (cm)</Label>
                <Input
                  id="mat-cw"
                  type="number"
                  min={5}
                  max={500}
                  className="w-28"
                  placeholder="mis. 120"
                  value={customW}
                  disabled={generate.isPending}
                  onChange={(e) => setCustomW(e.target.value)}
                />
              </div>
              <span className="pb-2.5 text-slate-400">×</span>
              <div className="space-y-1.5">
                <Label htmlFor="mat-ch">Tinggi (cm)</Label>
                <Input
                  id="mat-ch"
                  type="number"
                  min={5}
                  max={500}
                  className="w-28"
                  placeholder="mis. 40"
                  value={customH}
                  disabled={generate.isPending}
                  onChange={(e) => setCustomH(e.target.value)}
                />
              </div>
              <p className="pb-2.5 text-xs text-slate-500">
                5–500 cm. Tata letak menyesuaikan rasio secara otomatis.
              </p>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="mat-brief">Brief</Label>
            <Textarea
              id="mat-brief"
              rows={5}
              placeholder="Contoh: Flyer webinar 'Webinar Produk', 25 Juli 2026 pukul 13.00 WIB, online via Zoom, gratis. Daftar di acme.example/webinar. Narahubung 0812-xxxx."
              value={brief}
              disabled={generate.isPending}
              onChange={(e) => setBrief(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mat-files">Lampiran (opsional)</Label>
            <input
              key={fileKey}
              id="mat-files"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,.txt,.md,.pdf,text/plain,text/markdown,application/pdf"
              disabled={generate.isPending}
              onChange={(e) =>
                setAttachFiles(Array.from(e.target.files ?? []).slice(0, 5))
              }
              className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm file:mr-3 file:rounded-md file:border-0 file:bg-navy file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
            />
            <p className="text-xs text-slate-500">
              Lampirkan <strong>gambar</strong> (dipakai sebagai latar materi,
              menggantikan Pexels/Galeri) atau dokumen{" "}
              <strong>PDF / teks / .md</strong> (teksnya jadi referensi isi untuk
              AI). Maks 5 file.
            </p>
            {attachFiles.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {attachFiles.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                  >
                    <Paperclip className="h-3 w-3 text-slate-400" />
                    {f.name}
                    <button
                      type="button"
                      onClick={() =>
                        setAttachFiles(attachFiles.filter((_, j) => j !== i))
                      }
                      className="text-slate-400 hover:text-coral"
                      aria-label={`Hapus ${f.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {withPhoto ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_14rem]">
              <div className="space-y-1.5">
                <Label htmlFor="mat-imgq">
                  Kata kunci foto (opsional, English)
                </Label>
                <Input
                  id="mat-imgq"
                  placeholder="e.g. business conference, modern office"
                  value={imageQuery}
                  disabled={generate.isPending || imageSource === "library"}
                  onChange={(e) => setImageQuery(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  {imageSource === "library"
                    ? "Sumber = Galeri: AI memilih foto dari galeri Anda (kata kunci diabaikan)."
                    : "Kosongkan agar AI memilih foto Pexels yang relevan."}
                </p>
              </div>
              <ImageSourceSelect
                id="mat-image-source"
                value={imageSource}
                onChange={setImageSource}
                disabled={generate.isPending}
              />
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Tanpa foto: latar memakai grafis brand (gradien navy–teal).
            </p>
          )}

          <div>
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={!canGenerate}
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Megaphone className="h-4 w-4" />
              )}
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-sm font-medium text-slate-500">
        Hasil <span className="capitalize">({accountKey})</span>
      </h2>

      {materialsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : materialsQuery.isError ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-coral">
            Gagal memuat materi.
          </CardContent>
        </Card>
      ) : materials.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-slate-500">
            Belum ada materi. Buat materi pertama Anda di atas.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {materials.map((m) => (
            <MaterialCard key={m.id} material={m} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One generated material: preview + status + download PNG/PDF + delete. */
function MaterialCard({ material }: { material: MarketingMaterialDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  // Resolve label + preview aspect from the preset, or from the custom mm size.
  const preset =
    material.format === "custom"
      ? undefined
      : MATERIAL_FORMATS[material.format];
  const cm = (mm: number) => (mm / 10).toString().replace(/\.0$/, "");
  const label =
    preset?.label ??
    (material.customWidthMm && material.customHeightMm
      ? `Custom ${cm(material.customWidthMm)}×${cm(material.customHeightMm)} cm`
      : "Custom");
  const aspectW = preset?.pxWidth ?? material.customWidthMm ?? 1;
  const aspectH = preset?.pxHeight ?? material.customHeightMm ?? 1;
  const aspect = `${aspectW} / ${aspectH}`;

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["materials"] });

  const remove = useMutation({
    mutationFn: () => api.deleteMaterial(material.id),
    onSuccess: () => {
      invalidate();
      toast.success("Materi dihapus");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menghapus"),
  });

  const regenerate = useMutation({
    mutationFn: () => api.regenerateMaterial(material.id),
    onSuccess: () => {
      invalidate();
      toast.success("Membuat variasi baru… (muncul di daftar)");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal membuat variasi"),
  });

  const fileBase = `${material.format}-${material.id}`;

  return (
    <Card>
      <div
        className="relative flex items-center justify-center overflow-hidden rounded-t-xl bg-slate-100"
        style={{ aspectRatio: aspect }}
      >
        {material.status === "ready" && material.hasImage ? (
          <img
            src={materialImageUrl(material)}
            alt={material.title ?? material.format}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        ) : material.status === "failed" ? (
          <div className="flex flex-col items-center gap-1 p-4 text-center text-coral">
            <AlertTriangle className="h-6 w-6" />
            <span className="text-xs">Gagal dibuat</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 p-4 text-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin text-teal" />
            <span className="text-xs">Sedang dibuat…</span>
          </div>
        )}
        <Badge variant="navy" className="absolute left-2 top-2">
          {label}
        </Badge>
      </div>
      <CardContent className="space-y-3 p-4">
        <div>
          <p className="line-clamp-1 font-medium text-slate-900">
            {material.title ?? "(tanpa judul)"}
          </p>
          <p className="line-clamp-2 text-xs text-slate-500">{material.brief}</p>
        </div>
        {material.attachments.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {material.attachments.map((a) => (
              <a
                key={a.id}
                href={materialAttachmentUrl(a)}
                target="_blank"
                rel="noreferrer"
                title={a.originalName}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200"
              >
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[8rem] truncate">{a.originalName}</span>
              </a>
            ))}
          </div>
        ) : null}
        {material.status === "failed" && material.errorMsg ? (
          <p className="line-clamp-2 text-xs text-coral">{material.errorMsg}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {material.status === "ready" && material.hasImage ? (
            <a
              href={materialImageUrl(material)}
              download={`${fileBase}.png`}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              PNG
            </a>
          ) : null}
          {material.status === "ready" && material.hasPdf ? (
            <a
              href={materialPdfUrl(material.id)}
              download={`${fileBase}.pdf`}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" />
              PDF
            </a>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || material.status === "generating"}
            title="Buat variasi baru dengan brief & pengaturan yang sama"
          >
            {regenerate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Buat ulang
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-coral hover:text-coral"
            onClick={() => {
              if (window.confirm("Hapus materi ini?")) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            {remove.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Hapus
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
